// Purchasing API
import api, { type AxiosResponse } from './client';
import type {
  PurchaseOrder,
  PurchaseOrderLine,
  PODocument,
  GRNDocument,
  ASNDocument,
  PurchasingDocumentDownload,
  ReasonCode,
  POLineSchedule,
  POChangeLog,
  POChangeRequestAttachment,
  GoodsReceiptNote,
  AdvancedShippingNotice,
  ASNTracking,
  Job,
  WriteBack,
  WriteBackStats,
  CustomerQuery,
  CustomerQueryType,
  CustomerQueryPriority,
  CustomerQueryStats,
  ForecastTableResponse,
  ForecastCellDetail,
  ForecastFilterOptions,
  POForecast,
  PartSchedule,
  PartScheduleEntry,
  ForecastHistory,
  PurchasingDashboardStats,
  ContractPrice,
  ContractPricePart,
  ContractPriceChangeLog,
  ContractPriceImportPreview,
  QueryParams,
  POLineMessage,
  ASNMessage,
  CommodityGroup,
  CommodityType,
  DeliveryLine,
  POLineExportColumn,
  POLineExportTemplate,
} from './types';

export const purchasingAPI = {
  // Purchase Orders
  getOrders: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: PurchaseOrder[] }>> =>
    api.get('/purchasing/orders/', { params }),

  // Export ALL filtered PO Headers (Sales Orders) with current filters. Defaults to xlsx.
  exportOrders: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/orders/export/', { params, responseType: 'blob' }),

  getOrder: (id: number): Promise<AxiosResponse<PurchaseOrder>> =>
    api.get(`/purchasing/orders/${id}/`),

  getDashboardStats: (): Promise<AxiosResponse<PurchasingDashboardStats>> =>
    api.get('/purchasing/orders/dashboard_stats/'),

  // Lazy-load modal data for dashboard charts
  getDashboardModalData: (modalType: string, params?: QueryParams): Promise<AxiosResponse<Record<string, unknown[]>>> =>
    api.get('/purchasing/orders/dashboard_modal_data/', { params: { modal_type: modalType, ...params } }),

  acknowledgeOrder: (id: number): Promise<AxiosResponse<PurchaseOrder>> =>
    api.post(`/purchasing/orders/${id}/acknowledge/`),

  updateStatus: (id: number, status: string, reason?: string): Promise<AxiosResponse<PurchaseOrder>> =>
    api.post(`/purchasing/orders/${id}/update_status/`, { status, reason }),

  // PO Lines
  getLines: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: PurchaseOrderLine[] }>> =>
    api.get('/purchasing/lines/', { params }),

  getLine: (id: number): Promise<AxiosResponse<PurchaseOrderLine>> =>
    api.get(`/purchasing/lines/${id}/`),

  // Released delivery lines (from the DNA ERP import → call_off.DeliveryLine).
  // Backs the Delivery Lines sub-tab. Defaults to lines not yet on an ASN
  // (`available=true`); pass `available=false` to include those already used.
  getDeliveryLines: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: DeliveryLine[] }>> =>
    api.get('/purchasing/delivery-lines/', { params }),

  exportDeliveryLines: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/delivery-lines/export/', { params, responseType: 'blob' }),

  // CR-1558: Export PO Lines with filters
  exportLines: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/lines/export/', { params, responseType: 'blob' }),

  // PO Lines tab badge counts in a single round-trip (replaces 4-call fan-out).
  // Mirrors the same filterset_fields as getLines, so pass the same filter
  // params you'd pass to the main list query.
  getLineTabCounts: (params?: QueryParams): Promise<AxiosResponse<{
    needs_action: number;
    open: number;
    closed: number;
    on_hold: number;
    completed: number;
    due_today: number;
    high_priority: number;
  }>> =>
    api.get('/purchasing/lines/tab-counts/', { params }),

  // CR-3413: Get filter options for PO Lines
  getLineFilterOptions: (): Promise<AxiosResponse<{
    customers: Array<{ id: number; name: string }>;
    suppliers: Array<{ id: number; name: string; group?: number | null }>;
    commodity_groups: Array<{ id: number; name: string }>;
    commodity_types: Array<{ id: number; name: string; commodity_group_id: number }>;
  }>> =>
    api.get('/purchasing/lines/filter-options/'),

  // PO Line Acknowledgment Workflow Actions
  // timeout: 120s — these trigger async Celery tasks on the backend which may take a moment
  acceptDeliveryDate: (lineId: number, data: { comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/accept-delivery-date/`, data, { timeout: 120000 }),

  acceptQuantityChange: (lineId: number, data: { comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/accept-quantity-change/`, data, { timeout: 120000 }),

  acceptPriceChange: (lineId: number, data: { comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/accept-price-change/`, data, { timeout: 120000 }),

  declineDeliveryDate: (lineId: number, data: { reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/decline-delivery-date/`, data, { timeout: 120000 }),

  // CR-4688: Per-field reject for partial acceptance/rejection of multi-field change requests
  rejectFieldChange: (lineId: number, data: { field: string; reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/reject-field-change/`, data, { timeout: 120000 }),

  // CR-4680: Customer cancels their own proposed field change
  cancelFieldChange: (lineId: number, data: { field: string; reason?: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/cancel-field-change/`, data, { timeout: 120000 }),

  proposeAlternativeDate: (lineId: number, data: { proposedDate: string; reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/propose-alternative-date/`, data),

  proposeQuantityChange: (lineId: number, data: { proposedQuantity: string; reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/propose-quantity-change/`, data),

  proposePriceChange: (lineId: number, data: { proposedPrice: string; reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/propose-price-change/`, data),

  // PO Line Hold Management
  putLineOnHold: (lineId: number, data: { reason: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/put-on-hold/`, data),

  releaseLineHold: (lineId: number): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/release-hold/`),

  // PO Line Critical Flag Management
  setLineCritical: (lineId: number, is_critical: boolean): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/set-critical/`, { is_critical }),

  // CR-5263 — line-level OTD manual override
  setLineOTDOverride: (
    lineId: number,
    payload: { overridden: boolean; override_value: boolean | null; reason?: string },
  ): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/set-otd-override/`, payload),

  // Trigger a full OTD-verdict recompute (Performance settings page button).
  recomputeOtd: (): Promise<AxiosResponse<{ status: string; message: string }>> =>
    api.post('/purchasing/lines/recompute-otd/'),

  // PO Line Enhancements - GRN, Shortage, and Batch Tracking
  getLineGRNSummary: (lineId: number): Promise<AxiosResponse<{
    total_grns: number;
    total_received: string;
    total_accepted: string;
    total_rejected: string;
    pending_inspection: number;
    grns: GoodsReceiptNote[];
  }>> =>
    api.get(`/purchasing/lines/${lineId}/grn_summary/`),

  recordLineShortage: (lineId: number, data: {
    shortage_qty: string | number;
    reason_id: number;
    notes?: string;
  }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/record_shortage/`, data),

  resolveLineShortage: (lineId: number, data: {
    resolution_notes?: string;
  }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/resolve_shortage/`, data),

  getLineBatchHistory: (lineId: number): Promise<AxiosResponse<{
    part_number: string;
    line_batch_number: string;
    packing_note_number: string;
    total_batches: number;
    grn_batches: Array<{
      id: number;
      grn_number: string;
      batch_number: string;
      receipt_date: string;
      quantity_received: string;
      quantity_accepted: string;
      quantity_rejected: string;
      inspection_status: string;
      packing_slip_number: string;
    }>;
  }>> =>
    api.get(`/purchasing/lines/${lineId}/batch_history/`),

  // PO Line Schedules (ASN)
  getSchedules: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: POLineSchedule[] }>> =>
    api.get('/purchasing/schedules/', { params }),

  getSchedule: (id: number): Promise<AxiosResponse<POLineSchedule>> =>
    api.get(`/purchasing/schedules/${id}/`),

  // Schedule collaboration actions
  acceptScheduleDate: (id: number, data: { comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: POLineSchedule }>> =>
    api.post(`/purchasing/schedules/${id}/accept-delivery-date/`, data),
  declineScheduleDate: (id: number, data: { reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: POLineSchedule }>> =>
    api.post(`/purchasing/schedules/${id}/decline-delivery-date/`, data),
  proposeScheduleDate: (id: number, data: { proposed_date: string; reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: POLineSchedule }>> =>
    api.post(`/purchasing/schedules/${id}/propose-alternative-date/`, data),
  proposeScheduleQty: (id: number, data: { proposed_quantity: number; reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: POLineSchedule }>> =>
    api.post(`/purchasing/schedules/${id}/propose-quantity-change/`, data),
  putScheduleOnHold: (id: number, data: { reason: string }): Promise<AxiosResponse<{ status: string; message: string; data: POLineSchedule }>> =>
    api.post(`/purchasing/schedules/${id}/put-on-hold/`, data),
  releaseScheduleHold: (id: number): Promise<AxiosResponse<{ status: string; message: string; data: POLineSchedule }>> =>
    api.post(`/purchasing/schedules/${id}/release-hold/`),

  // PHASE 1 - Reason Codes
  getReasonCodes: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ReasonCode[] }>> =>
    api.get('/purchasing/reason-codes/', { params }),
  createReasonCode: (data: { code: string; description: string; category: string; is_active?: boolean }): Promise<AxiosResponse<ReasonCode>> =>
    api.post('/purchasing/reason-codes/', data),
  updateReasonCode: (id: number, data: Partial<{ code: string; description: string; category: string; is_active: boolean }>): Promise<AxiosResponse<ReasonCode>> =>
    api.patch(`/purchasing/reason-codes/${id}/`, data),
  deleteReasonCode: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/reason-codes/${id}/`),

  getReasonCode: (id: number): Promise<AxiosResponse<ReasonCode>> =>
    api.get(`/purchasing/reason-codes/${id}/`),

  // SO/PO Lines Export Templates (admin-managed, company-shared)
  // Read-only registry of all available export columns for the column picker.
  getExportColumns: (): Promise<AxiosResponse<POLineExportColumn[]>> =>
    api.get('/purchasing/lines/export-columns/'),
  getExportTemplates: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: POLineExportTemplate[] }>> =>
    api.get('/purchasing/export-templates/', { params }),
  createExportTemplate: (data: { name: string; columns: string[]; is_active?: boolean }): Promise<AxiosResponse<POLineExportTemplate>> =>
    api.post('/purchasing/export-templates/', data),
  updateExportTemplate: (id: number, data: Partial<{ name: string; columns: string[]; is_active: boolean }>): Promise<AxiosResponse<POLineExportTemplate>> =>
    api.patch(`/purchasing/export-templates/${id}/`, data),
  deleteExportTemplate: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/export-templates/${id}/`),

  // PHASE 1 - Goods Receipt Notes (GRNs)
  getGRNs: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: GoodsReceiptNote[] }>> =>
    api.get('/purchasing/grns/', { params }),

  getGRN: (id: number): Promise<AxiosResponse<GoodsReceiptNote>> =>
    api.get(`/purchasing/grns/${id}/`),

  createGRN: (data: Partial<GoodsReceiptNote>): Promise<AxiosResponse<GoodsReceiptNote>> =>
    api.post('/purchasing/grns/', data),

  updateGRN: (id: number, data: Partial<GoodsReceiptNote>): Promise<AxiosResponse<GoodsReceiptNote>> =>
    api.put(`/purchasing/grns/${id}/`, data),

  // CR-3861: Export GRNs/Deliveries
  exportGRNs: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/grns/export/', { params, responseType: 'blob' }),

  // GRN Workflow Actions
  acknowledgeGRN: (id: number, data: { comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: GoodsReceiptNote }>> =>
    api.post(`/purchasing/grns/${id}/acknowledge/`, data),

  disputeGRN: (id: number, data: { reason_id: number; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: GoodsReceiptNote }>> =>
    api.post(`/purchasing/grns/${id}/dispute/`, data),

  resolveGRNDispute: (id: number, data: { comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: GoodsReceiptNote }>> =>
    api.post(`/purchasing/grns/${id}/resolve-dispute/`, data),

  // GRN Packing Slip Lookup
  getGRNsByPackingSlip: (packingSlipNumber: string): Promise<AxiosResponse<{
    status: string;
    message: string;
    count: number;
    data: GoodsReceiptNote[];
  }>> =>
    api.get('/purchasing/grns/by-packing-slip/', { params: { number: packingSlipNumber } }),

  // PHASE 1 - Advanced Shipping Notices (ASNs)
  getASNs: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: AdvancedShippingNotice[] }>> =>
    api.get('/purchasing/asns/', { params }),

  getASNFilterOptions: (): Promise<AxiosResponse<{
    couriers: Array<{ value: string; label: string }>;
  }>> =>
    api.get('/purchasing/asns/filter-options/'),

  getASN: (id: number): Promise<AxiosResponse<AdvancedShippingNotice>> =>
    api.get(`/purchasing/asns/${id}/`),

  createASN: (data: Record<string, unknown>): Promise<AxiosResponse<AdvancedShippingNotice>> =>
    api.post('/purchasing/asns/', data),

  /** Get available call-off delivery lines not yet linked to an ASN */

  /** CR-4888 (Slice D): download blank ASN delivery-line import template */
  downloadASNTemplate: (): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/asns/import-template/', { responseType: 'blob' }),

  /** CR-4888 (Slice D): upload populated ASN delivery-line spreadsheet */
  importASNLines: (formData: FormData): Promise<AxiosResponse<{
    total: number;
    imported: number;
    ignored: number;
    errors: number;
    error_details: Array<{ row: number; errors: string[] }>;
    delivery_line_ids: number[];
  }>> =>
    api.post('/purchasing/asns/import-lines/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateASN: (id: number, data: Partial<AdvancedShippingNotice>): Promise<AxiosResponse<AdvancedShippingNotice>> =>
    api.patch(`/purchasing/asns/${id}/`, data),

  // Export ASNs with current filters (CSV default, XLSX optional)
  exportASNs: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/asns/export/', { params, responseType: 'blob' }),

  // ASN Workflow Actions
  submitASN: (id: number): Promise<AxiosResponse<{ status: string; message: string; data: AdvancedShippingNotice }>> =>
    api.post(`/purchasing/asns/${id}/submit/`),

  acknowledgeASN: (id: number): Promise<AxiosResponse<{ status: string; message: string; data: AdvancedShippingNotice }>> =>
    api.post(`/purchasing/asns/${id}/acknowledge/`),

  markASNDelivered: (id: number, data: { actual_delivery_date?: string }): Promise<AxiosResponse<{ status: string; message: string; data: AdvancedShippingNotice }>> =>
    api.post(`/purchasing/asns/${id}/mark-delivered/`, data),

  calculateASNCarbon: (id: number): Promise<AxiosResponse<{ status: string; message: string; carbon_emission_kg: string; data: AdvancedShippingNotice }>> =>
    api.post(`/purchasing/asns/${id}/calculate-carbon/`),

  printASN: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/asns/${id}/print/`, { responseType: 'blob' }),

  /** Manufacturing jobs recorded against a PO line, with route operations. */
  getPOLineJobs: (lineId: number): Promise<AxiosResponse<{ count: number; results: Job[] }>> =>
    api.get(`/purchasing/lines/${lineId}/jobs/`),

  /**
   * Which of the given PO lines have imported ERP jobs.
   *
   * One request for a whole shipment's lines, so a screen covering many lines
   * can offer the job documents only where they will actually generate.
   *
   * @param ids - PO line ids to check (max 200 per call)
   * @returns The subset that have jobs, ascending
   */
  getLinesWithJobs: (ids: number[]): Promise<AxiosResponse<number[]>> =>
    api.get('/purchasing/lines/with-jobs/', { params: { ids: ids.join(',') } }),

  /** Certificate of Conformity PDF for a PO line, built from its ERP jobs. */
  getPOLineCertificate: (lineId: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/lines/${lineId}/certificate/`, { responseType: 'blob' }),

  /** Despatch Note / Proof of Delivery PDF for a PO line. */
  getPOLineDespatchNote: (lineId: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/lines/${lineId}/despatch-note/`, { responseType: 'blob' }),

  // Tracking PRO — live courier tracking
  /** Full courier tracking state and scan timeline. 404s when never registered. */
  getASNTracking: (id: number): Promise<AxiosResponse<ASNTracking>> =>
    api.get(`/purchasing/asns/${id}/tracking/`),

  /** Force a live re-read of courier status; registers the ASN if needed. */
  refreshASNTracking: (id: number): Promise<AxiosResponse<ASNTracking>> =>
    api.post(`/purchasing/asns/${id}/refresh-tracking/`),

  // Get ASNs for a specific PO line
  getLineASNs: (lineId: number): Promise<AxiosResponse<{
    status: string;
    total_asns: number;
    total_shipped: string;
    asns: AdvancedShippingNotice[];
  }>> =>
    api.get('/purchasing/asns/by-po-line/', { params: { po_line_id: lineId } }),

  // PHASE 1 - Changelog
  getChangelogs: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: POChangeLog[] }>> =>
    api.get('/purchasing/changelog/', { params }),

  getChangelog: (id: number): Promise<AxiosResponse<POChangeLog>> =>
    api.get(`/purchasing/changelog/${id}/`),

  // CR-4865: PO Change Request attachments
  getChangeRequestAttachments: (params: { po_line?: number; change_request_ref?: number }):
    Promise<AxiosResponse<{ count: number; results: POChangeRequestAttachment[] }>> =>
    api.get('/purchasing/po-change-request-attachments/', { params }),

  uploadChangeRequestAttachment: (data: {
    po_line: number;
    change_request_ref: number;
    side: 'submission' | 'decision';
    file: File;
  }): Promise<AxiosResponse<POChangeRequestAttachment>> => {
    const formData = new FormData();
    formData.append('po_line', String(data.po_line));
    formData.append('change_request_ref', String(data.change_request_ref));
    formData.append('side', data.side);
    formData.append('file', data.file);
    return api.post('/purchasing/po-change-request-attachments/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteChangeRequestAttachment: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/po-change-request-attachments/${id}/`),

  downloadChangeRequestAttachment: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/po-change-request-attachments/${id}/download/`, { responseType: 'blob' }),

  // CR-4880: proxy download for contract attachments (avoids S3/URL issues on direct link)
  downloadContractFile: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/contract-prices/${id}/download-file/`, { responseType: 'blob' }),

  // Data Import
  importData: (formData: FormData): Promise<AxiosResponse<{
    success: boolean;
    message: string;
    data: {
      customers_created: number;
      customers_updated: number;
      pos_created: number;
      lines_created: number;
      grns_created: number;
      grns_skipped: number;
      errors: string[];
    };
  }>> => {
    return api.post('/purchasing/import-data/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600000, // 10 min for large imports
    });
  },

  getImportTemplates: (): Promise<AxiosResponse<{
    templates: {
      customers: {
        description: string;
        required_columns: string[];
        sheet_name: string;
      };
      purchase_orders: {
        description: string;
        required_columns: string[];
      };
      grns: {
        description: string;
        required_columns: string[];
      };
    };
  }>> =>
    api.get('/purchasing/import-templates/'),

  downloadImportTemplates: (type?: string): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/download-templates/', { responseType: 'blob', params: type ? { type } : {} }),

  // Paragon ERP Integration - WriteBack API
  getWriteBacks: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: WriteBack[] }>> =>
    api.get('/purchasing/writebacks/', { params }),

  getWriteBack: (id: number): Promise<AxiosResponse<WriteBack>> =>
    api.get(`/purchasing/writebacks/${id}/`),

  markWriteBackSynced: (id: number): Promise<AxiosResponse<{ status: string; synced_at: string; message: string }>> =>
    api.post(`/purchasing/writebacks/${id}/mark_synced/`),

  bulkMarkWriteBacksSynced: (ids: number[]): Promise<AxiosResponse<{ status: string; synced_count: number; message: string }>> =>
    api.post('/purchasing/writebacks/bulk_mark_synced/', { ids }),

  exportWriteBacks: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/writebacks/export/', { params, responseType: 'blob' }),

  getWriteBackStats: (): Promise<AxiosResponse<WriteBackStats>> =>
    api.get('/purchasing/writebacks/stats/'),

  // Contract Pricing
  getContractPrices: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ContractPrice[] }>> =>
    api.get('/purchasing/contract-prices/', { params }),

  getContractPrice: (id: number): Promise<AxiosResponse<ContractPrice>> =>
    api.get(`/purchasing/contract-prices/${id}/`),

  createContractPrice: (data: Partial<ContractPrice>): Promise<AxiosResponse<ContractPrice>> =>
    api.post('/purchasing/contract-prices/', data),

  updateContractPrice: (id: number, data: Partial<ContractPrice>): Promise<AxiosResponse<ContractPrice>> =>
    api.patch(`/purchasing/contract-prices/${id}/`, data),

  updateContractPriceFormData: (id: number, data: FormData): Promise<AxiosResponse<ContractPrice>> =>
    api.patch(`/purchasing/contract-prices/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteContractPrice: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/contract-prices/${id}/`),

  getContractPriceChangelog: (id: number, params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ContractPriceChangeLog[] }>> =>
    api.get(`/purchasing/contract-prices/${id}/changelog/`, { params }),

  exportContractPrices: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/contract-prices/export/', { params, responseType: 'blob' }),

  importContractPricesPreview: (formData: FormData): Promise<AxiosResponse<{
    status: string;
    message: string;
    total_rows: number;
    error_count: number;
    errors: Array<{ row: number; errors: string[] }>;
    preview: ContractPriceImportPreview[];
    column_mapping: Record<string, string>;
  }>> =>
    api.post('/purchasing/contract-prices/import-preview/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  importContractPricesConfirm: (data: { rows: ContractPriceImportPreview[] }): Promise<AxiosResponse<{
    status: string;
    message: string;
    created_contracts: number;
    created_parts: number;
    updated_parts: number;
    errors: Array<{ contract_number: string; error: string }>;
  }>> =>
    api.post('/purchasing/contract-prices/import-confirm/', data),

  // Contract Pricing Parts
  getContractPriceParts: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ContractPricePart[] }>> =>
    api.get('/purchasing/contract-price-parts/', { params }),

  getContractPricePart: (id: number): Promise<AxiosResponse<ContractPricePart>> =>
    api.get(`/purchasing/contract-price-parts/${id}/`),

  createContractPricePart: (data: Partial<ContractPricePart>): Promise<AxiosResponse<ContractPricePart>> =>
    api.post('/purchasing/contract-price-parts/', data),

  updateContractPricePart: (id: number, data: Partial<ContractPricePart>): Promise<AxiosResponse<ContractPricePart>> =>
    api.put(`/purchasing/contract-price-parts/${id}/`, data),

  deleteContractPricePart: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/contract-price-parts/${id}/`),

  // CR-3381: PO Document Management
  getPODocuments: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: PODocument[] }>> =>
    api.get('/purchasing/documents/', { params }),

  getPODocument: (id: number): Promise<AxiosResponse<PODocument>> =>
    api.get(`/purchasing/documents/${id}/`),

  uploadPODocument: (data: FormData): Promise<AxiosResponse<PODocument>> =>
    api.post('/purchasing/documents/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updatePODocument: (id: number, data: FormData): Promise<AxiosResponse<PODocument>> =>
    api.patch(`/purchasing/documents/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deletePODocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/documents/${id}/`),

  downloadPODocument: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/documents/${id}/download/`, { responseType: 'blob' }),

  getPODocumentsByPO: (poId: number): Promise<AxiosResponse<{
    documents: PODocument[];
    grouped: Record<string, Record<string, PODocument[]>>;
    total_count: number;
  }>> =>
    api.get('/purchasing/documents/by_po/', { params: { po_id: poId } }),

  getPODocumentTypes: (): Promise<AxiosResponse<{
    supplier_types: Array<{ value: string; label: string }>;
    customer_types: Array<{ value: string; label: string }>;
  }>> =>
    api.get('/purchasing/documents/document_types/'),

  // CR-3381: GRN Document Management
  getGRNDocuments: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: GRNDocument[] }>> =>
    api.get('/purchasing/grn-documents/', { params }),

  getGRNDocument: (id: number): Promise<AxiosResponse<GRNDocument>> =>
    api.get(`/purchasing/grn-documents/${id}/`),

  uploadGRNDocument: (data: FormData): Promise<AxiosResponse<GRNDocument>> =>
    api.post('/purchasing/grn-documents/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateGRNDocument: (id: number, data: FormData): Promise<AxiosResponse<GRNDocument>> =>
    api.patch(`/purchasing/grn-documents/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteGRNDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/grn-documents/${id}/`),

  downloadGRNDocument: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/grn-documents/${id}/download/`, { responseType: 'blob' }),

  getGRNDocumentsByGRN: (grnId: number): Promise<AxiosResponse<{
    documents: GRNDocument[];
    grouped: Record<string, GRNDocument[]>;
    total_count: number;
  }>> =>
    api.get('/purchasing/grn-documents/by_grn/', { params: { grn_id: grnId } }),

  getGRNDocumentTypes: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/purchasing/grn-documents/document_types/'),

  // CR-3381: ASN Document Management
  getASNDocuments: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ASNDocument[] }>> =>
    api.get('/purchasing/asn-documents/', { params }),

  getASNDocument: (id: number): Promise<AxiosResponse<ASNDocument>> =>
    api.get(`/purchasing/asn-documents/${id}/`),

  uploadASNDocument: (data: FormData): Promise<AxiosResponse<ASNDocument>> =>
    api.post('/purchasing/asn-documents/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateASNDocument: (id: number, data: FormData): Promise<AxiosResponse<ASNDocument>> =>
    api.patch(`/purchasing/asn-documents/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteASNDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/asn-documents/${id}/`),

  downloadASNDocument: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/asn-documents/${id}/download/`, { responseType: 'blob' }),

  getASNDocumentsByASN: (asnId: number): Promise<AxiosResponse<{
    documents: ASNDocument[];
    grouped: Record<string, ASNDocument[]>;
    total_count: number;
  }>> =>
    api.get('/purchasing/asn-documents/by_asn/', { params: { asn_id: asnId } }),

  getASNDocumentTypes: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/purchasing/asn-documents/document_types/'),

  // CR-3381: Document Download Tracking
  getDocumentDownloads: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: PurchasingDocumentDownload[] }>> =>
    api.get('/purchasing/document-downloads/', { params }),

  getDocumentDownloadsByDocument: (entityType: 'po' | 'grn' | 'asn', documentId: number): Promise<AxiosResponse<{
    downloads: PurchasingDocumentDownload[];
    total_count: number;
  }>> =>
    api.get('/purchasing/document-downloads/by_document/', { params: { entity_type: entityType, document_id: documentId } }),

  // CR-3388: Customer Query System
  getCustomerQueries: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: CustomerQuery[] }>> =>
    api.get('/purchasing/customer-queries/', { params }),

  getCustomerQuery: (id: number): Promise<AxiosResponse<CustomerQuery>> =>
    api.get(`/purchasing/customer-queries/${id}/`),

  createCustomerQuery: (data: {
    po_line: number;
    query_type: CustomerQueryType;
    query_text: string;
    reference_value?: string;
    priority?: CustomerQueryPriority;
    reason_code?: number;
  }): Promise<AxiosResponse<CustomerQuery>> =>
    api.post('/purchasing/customer-queries/', data),

  updateCustomerQuery: (id: number, data: Partial<CustomerQuery>): Promise<AxiosResponse<CustomerQuery>> =>
    api.patch(`/purchasing/customer-queries/${id}/`, data),

  deleteCustomerQuery: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/customer-queries/${id}/`),

  acknowledgeCustomerQuery: (id: number): Promise<AxiosResponse<{ status: string; message: string; data: CustomerQuery }>> =>
    api.post(`/purchasing/customer-queries/${id}/acknowledge/`),

  respondToCustomerQuery: (id: number, data: { response_text: string; mark_resolved?: boolean }): Promise<AxiosResponse<{ status: string; message: string; data: CustomerQuery }>> =>
    api.post(`/purchasing/customer-queries/${id}/respond/`, data),

  resolveCustomerQuery: (id: number, data: { resolution_notes?: string }): Promise<AxiosResponse<{ status: string; message: string; data: CustomerQuery }>> =>
    api.post(`/purchasing/customer-queries/${id}/resolve/`, data),

  getCustomerQueriesByPOLine: (poLineId: number): Promise<AxiosResponse<{ status: string; count: number; data: CustomerQuery[] }>> =>
    api.get('/purchasing/customer-queries/by-po-line/', { params: { po_line_id: poLineId } }),

  getCustomerQueryStats: (): Promise<AxiosResponse<CustomerQueryStats>> =>
    api.get('/purchasing/customer-queries/stats/'),

  // CR-3414: Forecast Tab
  getForecastTableData: (params?: {
    company_type?: 'supplier' | 'customer';
    part_number?: string;
    commodity_group?: string;
    customer_id?: number;
    supplier_id?: number;
    page?: number;
    page_size?: number;
  }): Promise<AxiosResponse<ForecastTableResponse>> =>
    api.get('/purchasing/forecasts/table-data/', { params }),

  getForecastCellDetail: (params: {
    supplier_id?: number;
    customer_id?: number;
    part_number: string;
    record_type: 1 | 2;
    date_bucket: string;
    page?: number;
    page_size?: number;
  }): Promise<AxiosResponse<{ results: ForecastCellDetail[]; total_count: number }>> =>
    api.get('/purchasing/forecasts/cell-detail/', { params }),

  getForecastFilterOptions: (): Promise<AxiosResponse<ForecastFilterOptions>> =>
    api.get('/purchasing/forecasts/filter-options/'),

  importForecasts: (data: FormData): Promise<AxiosResponse<{ status: string; imported: number; errors: string[] }>> =>
    api.post('/purchasing/forecasts/import/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Granular file-based imports (all share the same response shape)
  // Use 10-minute timeout for large file imports
  importCommodityGroups: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-commodity-groups/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  importFaultCodes: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-fault-codes/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  importDepartments: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-departments/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  importCustomerAdditional: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-customer-additional/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  importNCRs: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-ncrs/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  importForecastsFile: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-forecasts-file/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  importUsers: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-users/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  // CR-3413: Commodity Management CRUD
  getCommodityGroups: (params?: QueryParams): Promise<AxiosResponse<CommodityGroup[]>> =>
    api.get('/purchasing/commodity-groups/', { params }),

  createCommodityGroup: (data: { name: string; enabled?: boolean }): Promise<AxiosResponse<CommodityGroup>> =>
    api.post('/purchasing/commodity-groups/', data),

  updateCommodityGroup: (id: number, data: { name?: string; enabled?: boolean }): Promise<AxiosResponse<CommodityGroup>> =>
    api.patch(`/purchasing/commodity-groups/${id}/`, data),

  deleteCommodityGroup: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/commodity-groups/${id}/`),

  getCommodityTypes: (params?: QueryParams): Promise<AxiosResponse<CommodityType[]>> =>
    api.get('/purchasing/commodity-types/', { params }),

  createCommodityType: (data: { name: string; commodity_group: number; enabled?: boolean }): Promise<AxiosResponse<CommodityType>> =>
    api.post('/purchasing/commodity-types/', data),

  updateCommodityType: (id: number, data: { name?: string; commodity_group?: number; enabled?: boolean }): Promise<AxiosResponse<CommodityType>> =>
    api.patch(`/purchasing/commodity-types/${id}/`, data),

  deleteCommodityType: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/commodity-types/${id}/`),

  getCommodityCustomerMap: (): Promise<AxiosResponse<Record<string, { groups: number[]; types: number[] }>>> =>
    api.get('/purchasing/commodity-groups/customer-map/'),

  exportForecasts: (params?: {
    part_number?: string;
    commodity_group?: string;
  }): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/forecasts/export/', { params, responseType: 'blob' }),

  // Forecast CRUD
  getForecasts: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: POForecast[] }>> =>
    api.get('/purchasing/forecasts/', { params }),

  getForecast: (id: number): Promise<AxiosResponse<POForecast>> =>
    api.get(`/purchasing/forecasts/${id}/`),

  createForecast: (data: Partial<POForecast>): Promise<AxiosResponse<POForecast>> =>
    api.post('/purchasing/forecasts/', data),

  updateForecast: (id: number, data: Partial<POForecast>): Promise<AxiosResponse<POForecast>> =>
    api.patch(`/purchasing/forecasts/${id}/`, data),

  deleteForecast: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/forecasts/${id}/`),

  // Part Schedules (Firm Boundary Configuration)
  getPartSchedules: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: PartSchedule[] }>> =>
    api.get('/purchasing/part-schedules/', { params }),

  getPartSchedule: (id: number): Promise<AxiosResponse<PartSchedule>> =>
    api.get(`/purchasing/part-schedules/${id}/`),

  createPartSchedule: (data: Partial<PartSchedule>): Promise<AxiosResponse<PartSchedule>> =>
    api.post('/purchasing/part-schedules/', data),

  updatePartSchedule: (id: number, data: Partial<PartSchedule>): Promise<AxiosResponse<PartSchedule>> =>
    api.patch(`/purchasing/part-schedules/${id}/`, data),

  deletePartSchedule: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/part-schedules/${id}/`),

  // Part Schedule Entries (isolated part-level module, per-supplier opt-in)
  getPartScheduleEntries: (
    params?: QueryParams,
  ): Promise<AxiosResponse<{ count: number; results: PartScheduleEntry[] }>> =>
    api.get('/purchasing/part-schedule-entries/', { params }),

  getPartScheduleEntry: (id: number): Promise<AxiosResponse<PartScheduleEntry>> =>
    api.get(`/purchasing/part-schedule-entries/${id}/`),

  // Forecast History (Snapshots for Variance Analysis)
  getForecastHistory: (params?: {
    part_number?: string;
    snapshot_date?: string;
    page?: number;
    page_size?: number;
  }): Promise<AxiosResponse<{ count: number; results: ForecastHistory[] }>> =>
    api.get('/purchasing/forecast-history/', { params }),

  // CR-3409: PO Line Communication Log
  getLineMessages: (params: { po_line: number; page?: number; page_size?: number }): Promise<AxiosResponse<{ count: number; results: POLineMessage[] }>> =>
    api.get('/purchasing/messages/', { params: { ...params, ordering: 'sent_date' } }),

  createLineMessage: (data: { po: number; po_line: number; message_text: string }): Promise<AxiosResponse<POLineMessage>> =>
    api.post('/purchasing/messages/', data),

  markMessageRead: (id: number): Promise<AxiosResponse<POLineMessage>> =>
    api.post(`/purchasing/messages/${id}/mark_read/`),

  markMessageComplete: (id: number): Promise<AxiosResponse<POLineMessage>> =>
    api.post(`/purchasing/messages/${id}/mark_complete/`),

  markAllMessagesRead: (poLineId: number): Promise<AxiosResponse<{ marked_read: number }>> =>
    api.post('/purchasing/messages/mark-all-read/', { po_line: poLineId }),

  // CR-2220: Strikethrough and soft delete
  deleteLineMessage: (id: number): Promise<AxiosResponse<POLineMessage>> =>
    api.post(`/purchasing/messages/${id}/delete_message/`),

  strikethroughLineMessage: (id: number): Promise<AxiosResponse<POLineMessage>> =>
    api.post(`/purchasing/messages/${id}/strikethrough/`),

  removeStrikethroughLineMessage: (id: number): Promise<AxiosResponse<POLineMessage>> =>
    api.post(`/purchasing/messages/${id}/remove_strikethrough/`),

  // ASN Communication
  getASNMessages: (params: { asn: number; page?: number; page_size?: number }): Promise<AxiosResponse<{ count: number; results: ASNMessage[] }>> =>
    api.get('/purchasing/asn-messages/', { params: { ...params, ordering: 'sent_date' } }),

  createASNMessage: (data: { asn: number; message_text: string }): Promise<AxiosResponse<ASNMessage>> =>
    api.post('/purchasing/asn-messages/', data),

  // Delivery Appeals
  getDeliveryAppeals: (params?: QueryParams) =>
    api.get('/purchasing/delivery-appeals/', { params }),
  getDeliveryAppeal: (id: number) =>
    api.get(`/purchasing/delivery-appeals/${id}/`),
  createDeliveryAppeal: (data: { grn: number; qty_claimed?: string; date_claimed?: string; odd_claimed?: string; customer_note: string }) =>
    api.post('/purchasing/delivery-appeals/', data),
  respondToDeliveryAppeal: (id: number, data: { action: 'accept' | 'query' | 'reject'; response_note?: string }) =>
    api.post(`/purchasing/delivery-appeals/${id}/respond/`, data),
  updateDeliveryAppeal: (id: number, data: { qty_claimed?: string; date_claimed?: string; odd_claimed?: string; customer_note?: string }) =>
    api.patch(`/purchasing/delivery-appeals/${id}/`, data),
  cancelDeliveryAppeal: (id: number) =>
    api.post(`/purchasing/delivery-appeals/${id}/cancel/`),
  getDeliveryAppealLogs: (id: number) =>
    api.get(`/purchasing/delivery-appeals/${id}/logs/`),
  getAppealAttachments: (appealId: number) =>
    api.get(`/purchasing/delivery-appeals/${appealId}/attachments/`),
  uploadAppealAttachment: (appealId: number, formData: FormData) =>
    api.post(`/purchasing/delivery-appeals/${appealId}/attachments/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  downloadAppealAttachment: (appealId: number, attachmentId: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/delivery-appeals/${appealId}/attachments/${attachmentId}/download/`, { responseType: 'blob' }),
  deleteAppealAttachment: (appealId: number, attachmentId: number) =>
    api.delete(`/purchasing/delivery-appeals/${appealId}/attachments/${attachmentId}/`),
  exportDeliveryAppeals: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/delivery-appeals/export/', { params, responseType: 'blob' }),

  // Import customer promise dates from CSV/Excel
  importPromiseDates: (formData: FormData): Promise<AxiosResponse<{ total: number; updated: number; ignored: number; errors: number }>> =>
    api.post('/purchasing/lines/import_promise_dates/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Dashboard Milestones
  getMilestones: () =>
    api.get('/purchasing/milestones/'),
  createMilestone: (data: { title: string; milestone_date: string; milestone_type?: number }) =>
    api.post('/purchasing/milestones/', data),
  updateMilestone: (id: number, data: { title?: string; milestone_date?: string }) =>
    api.patch(`/purchasing/milestones/${id}/`, data),
  deleteMilestone: (id: number) =>
    api.delete(`/purchasing/milestones/${id}/`),
};
